import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import MediaCard from './MediaCard';
import 'react-calendar/dist/Calendar.css';
import '../styles/calendar.css';

// Mock data for local development
const MOCK_DATA = {
  pregnancy: [
    { id: '1', date: '2024-01-15', description: 'First ultrasound - saw the heartbeat!', mediaUrl: 'https://drive.google.com/uc?id=PLACEHOLDER1&export=view', mediaType: 'image' },
    { id: '2', date: '2024-02-20', description: 'Baby bump starting to show', mediaUrl: 'https://drive.google.com/uc?id=PLACEHOLDER2&export=view', mediaType: 'image' },
    { id: '3', date: '2024-03-10', description: 'Baby shower celebration', mediaUrl: 'https://drive.google.com/uc?id=PLACEHOLDER3&export=view', mediaType: 'image' },
  ],
  growth: [
    { id: '4', date: '2024-06-01', description: 'First day at home - so tiny!', mediaUrl: 'https://drive.google.com/uc?id=PLACEHOLDER4&export=view', mediaType: 'image' },
    { id: '5', date: '2024-07-15', description: 'First smile captured!', mediaUrl: 'https://drive.google.com/uc?id=PLACEHOLDER5&export=view', mediaType: 'image' },
    { id: '6', date: '2024-09-01', description: 'Started rolling over', mediaUrl: 'https://drive.google.com/uc?id=PLACEHOLDER6&export=view', mediaType: 'image' },
  ]
};

function CalendarView({ journey, refresh, startDate, endDate }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateEntries, setSelectedDateEntries] = useState([]);

  useEffect(() => {
    fetchEntries();
  }, [journey, refresh, startDate, endDate]);

  useEffect(() => {
    if (selectedDate) {
      const dateStr = selectedDate.toISOString().split('T')[0];
      let dayEntries = entries.filter(entry => entry.date === dateStr);
      
      // Apply date range filters
      if (startDate && new Date(dateStr) < new Date(startDate)) {
        dayEntries = [];
      }
      if (endDate && new Date(dateStr) > new Date(endDate)) {
        dayEntries = [];
      }
      
      setSelectedDateEntries(dayEntries);
    }
  }, [selectedDate, entries, startDate, endDate]);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      // Check if running in local mode
      if (process.env.REACT_APP_LOCAL_MODE === 'true') {
        // Use mock data
        const mockEntries = MOCK_DATA[journey] || [];
        setEntries(mockEntries);
      } else {
        // Fetch from API Gateway
        const apiUrl = process.env.REACT_APP_API_GATEWAY_URL;
        const response = await fetch(`${apiUrl}/entries?journey=${journey}`);
        const data = await response.json();
        setEntries(data.entries);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const tileContent = ({ date }) => {
    const dateStr = date.toISOString().split('T')[0];
    
    // Check if date is within range filter
    if (startDate && date < new Date(startDate)) return null;
    if (endDate && date > new Date(endDate)) return null;
    
    const hasEntry = entries.some(entry => entry.date === dateStr);
    return hasEntry ? <div className="dot"></div> : null;
  };

  if (loading) {
    return <div className="text-center py-12">Loading entries...</div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Calendar */}
      <div className="lg:col-span-1 bg-white rounded-lg shadow-md p-6">
        <Calendar
          onChange={setSelectedDate}
          value={selectedDate}
          tileContent={tileContent}
          maxDate={new Date()}
        />
      </div>

      {/* Selected Date Entries */}
      <div className="lg:col-span-2">
        {selectedDate ? (
          <>
            <h2 className="text-2xl font-bold mb-4 text-gray-800">
              {selectedDate.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </h2>
            {selectedDateEntries.length === 0 ? (
              <div className="bg-white rounded-lg p-8 text-center">
                <p className="text-gray-500">No entries for this date</p>
              </div>
            ) : (
              <div className="space-y-4">
                {selectedDateEntries.map((entry) => (
                  <MediaCard key={entry.id} entry={entry} viewType="calendar" />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg p-8 text-center">
            <p className="text-gray-500">Select a date to view entries</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CalendarView;

